import cx from 'classnames'

import { DIMENSION_COLORS, DIMENSION_LABELS, getHealthLevel } from '../types'
import { VariantScore } from '../types'

import ScoreBar from './ScoreBar'

type Props = {
  variant: VariantScore
  index: number
}

const VariantCard = ({ variant, index }: Props) => {
  const health = getHealthLevel(variant.overall)

  return (
    <div className="bg-gold-50 border border-gold-200 rounded-lg p-3 mb-2">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-semibold text-coolGray-400 uppercase tracking-wide">
          Variant {index + 1}
        </span>
        {variant.experimentName && (
          <span className="text-[10px] font-medium text-coolGray-500 truncate max-w-[260px]">
            · {variant.experimentName}
          </span>
        )}
        <span className="ml-auto text-lg font-bold tracking-tight text-purple-700">
          {variant.overall}%
        </span>
        <span
          className={cx(
            'text-[10px] font-bold px-2 py-0.5 rounded uppercase',
            health.className
          )}
        >
          {health.label}
        </span>
      </div>

      <div className="text-sm leading-relaxed pb-3 mb-3 border-b border-gold-200">
        {variant.copy}
      </div>

      <div className="flex flex-col gap-1 mb-2">
        {variant.language && (
          <ScoreBar
            label={DIMENSION_LABELS.language}
            score={Math.round(variant.language.score)}
            color={DIMENSION_COLORS.language}
          />
        )}
        {variant.settings && (
          <ScoreBar
            label={DIMENSION_LABELS.settings}
            score={Math.round(variant.settings.score)}
            color={DIMENSION_COLORS.settings}
          />
        )}
        {variant.brand && (
          <ScoreBar
            label={DIMENSION_LABELS.brand}
            score={Math.round(variant.brand.score)}
            color={DIMENSION_COLORS.brand}
          />
        )}
        {variant.brief && (
          <ScoreBar
            label={DIMENSION_LABELS.brief}
            score={Math.round(variant.brief.score)}
            color={DIMENSION_COLORS.brief}
          />
        )}
      </div>

      {/* Language fluency + rule findings */}
      {variant.language?.fluency && (
        <div className="text-xs py-1.5 px-2 border-l-2 leading-relaxed border-purple-500 bg-purple-50 text-purple-900 mt-2">
          <span className="font-medium">
            Fluency · {Math.round(variant.language.fluency.score)}%
          </span>
          <div className="mt-0.5 opacity-90">
            {variant.language.fluency.explanation}
          </div>
        </div>
      )}

      {variant.language && variant.language.findings.length > 0 && (
        <div className="flex flex-col gap-1 mt-2">
          {variant.language.findings.map((finding, i) => (
            <div
              key={i}
              className="text-xs py-1.5 px-2 border-l-2 leading-relaxed border-amber-600 bg-amber-50 text-amber-800"
            >
              <span className="font-medium">{finding.label}</span>
              <div className="text-amber-700 mt-0.5">{finding.explanation}</div>
              {finding.suggestion && (
                <div className="text-amber-700 mt-0.5">
                  {finding.suggestion}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Config / settings findings */}
      {variant.settings && variant.settings.findings.length > 0 && (
        <div className="flex flex-col gap-1 mt-2">
          {variant.settings.findings.map((finding, i) => (
            <div
              key={i}
              className="text-xs py-1.5 px-2 border-l-2 leading-relaxed border-amber-600 bg-amber-50 text-amber-800"
            >
              <span className="font-medium">{finding.label}</span>
              <div className="text-amber-700 mt-0.5">{finding.explanation}</div>
              {finding.suggestion && (
                <div className="text-amber-700 mt-0.5">
                  {finding.suggestion}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {variant.settings &&
        variant.settings.findings.length === 0 &&
        (!variant.language || variant.language.findings.length === 0) && (
          <div className="text-xs py-1.5 px-2 border-l-2 leading-relaxed border-green-600 bg-green-50 text-green-800 mt-2">
            All applicable language rules and config checks passed.
          </div>
        )}

      {/* Brand tone breakdown */}
      {variant.brand && variant.brand.tones.length > 0 && (
        <div className="flex flex-col gap-1 mt-2">
          {variant.brand.tones.map((tone, i) => (
            <div
              key={i}
              className={cx(
                'text-xs py-1.5 px-2 border-l-2 leading-relaxed',
                tone.score >= 75
                  ? 'border-green-600 bg-green-50 text-green-800'
                  : 'border-amber-600 bg-amber-50 text-amber-800'
              )}
            >
              <span className="font-medium">
                {tone.name} · {Math.round(tone.score)}%
              </span>
              <div className="mt-0.5 opacity-90">{tone.explanation}</div>
            </div>
          ))}
        </div>
      )}

      {/* Brief feedback */}
      {variant.brief && (
        <div
          className={cx(
            'text-xs py-1.5 px-2 border-l-2 leading-relaxed mt-2',
            variant.brief.score >= 75
              ? 'border-green-600 bg-green-50 text-green-800'
              : 'border-amber-600 bg-amber-50 text-amber-800'
          )}
        >
          <span className="font-medium">{DIMENSION_LABELS.brief}</span>
          <div className="mt-0.5 opacity-90">{variant.brief.explanation}</div>
        </div>
      )}
    </div>
  )
}

export default VariantCard
